/* eslint-disable complexity */
import { Component, isValidElement } from 'react';
import { DEFAULT_ERROR_FALLBACK as ErrorFallback } from '~/config/app.constants';
import { TErrorBoundaryProps, TErrorBoundaryState } from './config/types';
import { getExtendedLogger } from '@mindtickle/ui-shell-core/Logger';
import { logErrorToIntegrationTools } from './tools';
import logger from '~/config/app.logger';
import withNamespacing from '@mindtickle/hocs/lib/withNamespacing';
import { theme } from '~/config/app.constants';

const log = getExtendedLogger('error-boundary', logger);

const INITIAL_STATE: TErrorBoundaryState = {
  error: null,
  componentStack: null,
};

class ErrorBoundary extends Component<TErrorBoundaryProps, TErrorBoundaryState> {
  public constructor(props: TErrorBoundaryProps) {
    super(props);
    this.state = INITIAL_STATE;
  }

  public componentDidCatch(error: Error, { componentStack }: React.ErrorInfo): void {
    const { onError, config = {} } = this.props;

    if (onError) {
      onError(error, componentStack);
    }

    this.setState({ error, componentStack });

    log('error captured in shell error boundary %o with %o additional values', error, config);

    logErrorToIntegrationTools(error, { errorCapturedInShellErrorBoundary: true, ...config });
  }

  public resetErrorBoundary: () => void = () => {
    const { onReset } = this.props;
    const { error, componentStack } = this.state;
    if (onReset) {
      onReset(error, componentStack);
    }
    this.setState(INITIAL_STATE);
  };

  public render(): React.ReactNode {
    const { fallback, children, message = '' } = this.props;
    const state = this.state;

    if (state.error && state.componentStack) {
      let element: React.ReactElement | undefined = undefined;

      if (typeof fallback === 'function') {
        element = fallback({
          error: state.error,
          componentStack: state.componentStack,
          resetError: this.resetErrorBoundary,
        });
      } else {
        element = fallback;
      }

      if (isValidElement(element)) {
        return element;
      }

      // Show default fallback if no fallback provided or is not valid
      return <ErrorFallback message={message} error={state.error} />;
    }

    return children;
  }
}

function withErrorBoundary<P extends {}>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: TErrorBoundaryProps
): React.ComponentType<P> {
  const Wrapped: React.FC<P> = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  return withNamespacing(Wrapped, theme);
}

export default withNamespacing(ErrorBoundary, theme);
export { withErrorBoundary };
